import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { api } from "../api";
import { extractZones } from "../data/devices";
import ScheduleModal from "./ScheduleModal";
import "../components/ToggleButtons.css";
import "../components/css/schedules.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://test-automation.graymatrix.com:9443';

const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

function formatTime(time) {
  if (!time) return "—";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function formatDays(days) {
  if (!days || days.length === 0) return "—";
  if (days.length === 7) return "Every Day";
  return days.map(d => DAY_LABELS[d] || d).join(", ");
}

const DynamicPanel = () => {
  const { state } = useLocation();
  const {
    imgSrc,
    title,
    buttonNames    = [],
    commandMapping = {},
  } = state || {};

  const [toggles, setToggles]         = useState(Array(buttonNames.length).fill(false));
  const [backendStatus, setBackendStatus] = useState(false);
  const [schedules, setSchedules]     = useState([]);
  const [schedLoading, setSchedLoading] = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editingSchedule, setEditing] = useState(null);

  const zones = extractZones(commandMapping);

  // ── Backend health check ─────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/health`);
        setBackendStatus(res.data.status === "OK");
      } catch {
        setBackendStatus(false);
      }
    };
    check();
  }, []);

  // ── Load schedules for this area ─────────────────────────────────────────
  const loadSchedules = useCallback(async () => {
    setSchedLoading(true);
    try {
      const all = await api.getSchedules();
      setSchedules(all.filter(s => zones.includes(s.zone)));
    } catch {
      // silently fail — schedules are non-critical for manual control
    } finally {
      setSchedLoading(false);
    }
  }, [zones.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  // ── Manual toggle ────────────────────────────────────────────────────────
  const handleToggle = async (index) => {
    const updated = [...toggles];
    updated[index] = !updated[index];
    setToggles(updated);

    const name    = buttonNames[index];
    const cmdDef  = commandMapping[name];
    const rawCmd  = updated[index] ? cmdDef.on : cmdDef.off;
    const commands = Array.isArray(rawCmd) ? rawCmd : [rawCmd];

    for (const command of commands) {
      try {
        const res = await axios.post(`${BACKEND_URL}/`, {
          host: "192.168.0.53",
          port: 20108,
          command,
        });
        if (!res.data.success) {
          console.error(`Error toggling ${name}:`, res.data.error);
        }
      } catch (err) {
        console.error(`Network error for ${name}:`, err.message);
      }
    }
  };

  // ── Schedule CRUD ────────────────────────────────────────────────────────
  async function handleSaveSchedule(payload, id) {
    if (id) {
      await api.updateSchedule(id, payload);
    } else {
      await api.createSchedule(payload);
    }
    await loadSchedules();
  }

  async function handleDeleteSchedule(id) {
    if (!window.confirm("Delete this schedule?")) return;
    await api.deleteSchedule(id);
    setSchedules(prev => prev.filter(s => s.id !== id));
  }

  async function handleToggleSchedule(schedule) {
    const updated = schedule.enabled
      ? await api.disableSchedule(schedule.id)
      : await api.enableSchedule(schedule.id);
    setSchedules(prev => prev.map(s => s.id === updated.id ? updated : s));
  }

  return (
    <div className="vertical-toggle-container">
      <h2 className="vertical-toggle-title">{title}</h2>

      {imgSrc && (
        <img
          src={imgSrc}
          alt="Dynamic"
          style={{
            maxWidth: "55%",
            height: "auto",
            marginBottom: "20px",
            borderRadius: "12px",
            objectFit: "cover",
            opacity: 0.75,
            border: "1px solid #2c2c2c",
          }}
        />
      )}

      <p className="backend-status">
        Backend Status: {backendStatus ? "Online" : "Offline"}
      </p>

      {/* Manual controls */}
      <div className="vertical-toggle-list">
        {buttonNames.map((name, index) => (
          <div className="vertical-toggle-item" key={index}>
            <label className="toggle-label">{name}</label>
            <button
              className={`toggle-button ${toggles[index] ? "on" : "off"}`}
              onClick={() => handleToggle(index)}
            >
              {toggles[index] ? "On" : "Off"}
            </button>
          </div>
        ))}
      </div>

      {/* Schedules section */}
      <div className="panel-schedules">
        <div className="panel-schedules-header">
          <h3>Schedules</h3>
          <button
            className="btn-sm"
            onClick={() => { setEditing(null); setShowModal(true); }}
          >
            + Add
          </button>
        </div>

        {schedLoading && (
          <p style={{ fontSize: "0.85rem", color: "#5a7fa8" }}>Loading…</p>
        )}

        {!schedLoading && schedules.length === 0 && (
          <p style={{ fontSize: "0.85rem", color: "#5a7fa8" }}>
            No schedules for this area.
          </p>
        )}

        {schedules.map(s => (
          <div key={s.id} className={`panel-schedule-card${!s.enabled ? " disabled" : ""}`}>
            <div className="panel-schedule-info">
              <strong>{s.name || `${s.device_name} → ${s.action.toUpperCase()}`}</strong>
              <div className="panel-schedule-meta">
                {formatTime(s.time)} &nbsp;·&nbsp; {formatDays(s.days)}
                {!s.enabled && " · Disabled"}
              </div>
            </div>
            <div className="panel-schedule-actions">
              <button
                className="btn-sm"
                onClick={() => handleToggleSchedule(s)}
                title={s.enabled ? "Disable" : "Enable"}
              >
                {s.enabled ? "Disable" : "Enable"}
              </button>
              <button
                className="btn-sm"
                onClick={() => { setEditing(s); setShowModal(true); }}
              >
                Edit
              </button>
              <button
                className="btn-sm danger"
                onClick={() => handleDeleteSchedule(s.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <ScheduleModal
          schedule={editingSchedule}
          prefilledArea={!editingSchedule ? title : undefined}
          onClose={() => setShowModal(false)}
          onSave={handleSaveSchedule}
        />
      )}
    </div>
  );
};

export default DynamicPanel;
